/**
 * Turn a photo the user picked into a small square JPEG, in the browser.
 *
 * Avatars render at 96px at their largest, and phone cameras hand us 4-12MB
 * originals — uploading those would be slow on cellular and would blow past
 * the API's 1MB JSON body limit. Cropping and re-encoding here keeps the
 * upload small (tens of KB) and means the server never has to decode images.
 */

/** Longest edge of the stored avatar; 2x the largest place one is displayed. */
const AVATAR_SIZE = 512;

const AVATAR_QUALITY = 0.85;

export const AVATAR_CONTENT_TYPE = 'image/jpeg';

/** Anything larger than this is rejected before we try to decode it. */
export const MAX_AVATAR_FILE_BYTES = 15 * 1024 * 1024;

export type PreparedAvatar = {
  /** Base64 JPEG, no data-URL prefix — what the API expects. */
  imageData: string;
  contentType: string;
  /** Data URL of the same image, for previewing the pick before it's saved. */
  previewUrl: string;
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("That file couldn't be read as an image."));
    };
    image.src = objectUrl;
  });

/**
 * Center-crop to a square and scale down to AVATAR_SIZE. Throws with a
 * user-facing message when the file isn't a decodable image.
 */
export const prepareAvatarImage = async (file: File): Promise<PreparedAvatar> => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file.');
  }
  if (file.size > MAX_AVATAR_FILE_BYTES) {
    throw new Error('That image is too large. Please choose one under 15MB.');
  }

  const image = await loadImage(file);
  const source = Math.min(image.naturalWidth, image.naturalHeight);
  if (!source) {
    throw new Error("That file couldn't be read as an image.");
  }

  // Don't upscale a small original — it only costs bytes.
  const size = Math.min(source, AVATAR_SIZE);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error("This browser couldn't process that image.");
  }

  context.drawImage(
    image,
    (image.naturalWidth - source) / 2,
    (image.naturalHeight - source) / 2,
    source,
    source,
    0,
    0,
    size,
    size
  );

  const previewUrl = canvas.toDataURL(AVATAR_CONTENT_TYPE, AVATAR_QUALITY);

  return {
    imageData: previewUrl.slice(previewUrl.indexOf(',') + 1),
    contentType: AVATAR_CONTENT_TYPE,
    previewUrl,
  };
};
